import UploadIcon from "@mui/icons-material/UploadRounded";
import { CircularProgress, Typography } from "@mui/material";
import Button, { type ButtonProps } from "@mui/material/Button";
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import useId from "@mui/utils/useId";
import {
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { CircularProgressWithLabel } from "../CircularProgressWithLabel";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput";

export type DropZoneProps = {
  error?: boolean;
  buttonProps?: ButtonProps;
  disabled?: boolean;
  name: string;
  inputProps?: DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  handleFile: (
    file: File,
    setProgress?: (progress: number) => void
  ) => void | Promise<void>;
  label?: string;
};

export function DropZone({
  error,
  buttonProps,
  disabled,
  inputProps,
  name,
  handleFile,
  label,
}: DropZoneProps) {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:DropZone" });
  const dropText = t("dropText", "Drop file here");
  const defaultLabel = t("defaultLabel", "Click/drag file");
  const actualLabel = label || defaultLabel;
  const [isDragging, setIsDragging] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputId = useId();

  const handleFileList = async (
    fileList: FileList | File[] | null | undefined
  ) => {
    const files = structuredClone(fileList ?? []);
    if (files.length === 0) {
      return;
    }
    setIsChanging(true);
    try {
      for (const file of files) {
        await handleFile(file, setProgress);
      }
    } finally {
      setIsChanging(false);
      setProgress(0);
    }
  };

  function fileSystemFileEntryToFile(
    fileEntry: FileSystemFileEntry
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  }

  const onDrop = async (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const maybeFolder = e.dataTransfer?.items[0]?.webkitGetAsEntry();
    if (maybeFolder?.isDirectory) {
      const dirEntry = maybeFolder as FileSystemDirectoryEntry;
      const reader = dirEntry.createReader();
      reader.readEntries(async (entries) => {
        const filePromises: Promise<File>[] = [];
        for (const entry of entries) {
          if (entry.isFile) {
            filePromises.push(
              fileSystemFileEntryToFile(entry as FileSystemFileEntry)
            );
          }
        }
        const fileList = await Promise.all(filePromises);
        await handleFileList(fileList);
      });
      return;
    }
    await handleFileList(e.dataTransfer?.files);
  };
  return (
    <Button
      component="label"
      onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }
        setIsDragging(false);
      }}
      onDrop={(e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(false);
        onDrop(e);
      }}
      onKeyUp={(e: KeyboardEvent) => {
        if (
          e !== null &&
          e.key === "Enter" &&
          e.currentTarget !== null &&
          "click" in e.currentTarget &&
          typeof e.currentTarget.click === "function"
        ) {
          e.currentTarget.click();
        }
      }}
      disabled={disabled || isChanging}
      variant="outlined"
      color={error ? "error" : "primary"}
      {...buttonProps}
      sx={{
        minHeight: "100px",
        width: "100%",
        border: (theme) => {
          let color = isDragging
            ? theme.palette.primary.main
            : theme.palette.primary.lighter;
          if (error) {
            color = theme.palette.error.light;
          }
          return `1px dashed ${color}`;
        },
        [`&.${buttonBaseClasses.disabled}`]: {
          border: (theme) => `2px dashed ${theme.palette.divider}`,
        },
        ...buttonProps?.sx,
      }}
    >
      <Stack direction="column" alignItems="center" spacing={1}>
        {!isChanging && <UploadIcon fontSize="large" />}
        {isChanging &&
          (progress > 0 ? (
            <CircularProgressWithLabel value={progress} />
          ) : (
            <CircularProgress size={20} />
          ))}
        <Typography variant="button" textAlign="center">
          {isDragging ? dropText : actualLabel}
        </Typography>
      </Stack>
      <VisuallyHiddenInput
        type="file"
        multiple
        name={name}
        tabIndex={-1}
        id={inputId}
        onChange={async (e) => {
          await handleFileList(e.target.files);
        }}
        {...inputProps}
      />
    </Button>
  );
}
